import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { thaiTimeShort } from "./types";
import { cancelAttendanceConfirmation } from "@/lib/attendance-confirmation";
import { useLanguage } from "@/lib/i18n";
import { useState } from "react";

interface HomeModalsProps {
  confirmAction: any;
  confirmCopy: { title: string; body: string } | null;
  setConfirmAction: (action: any) => void;
  runConfirmedAction: () => void;
  checkInSuccess: boolean;
  lateReasonRequest: { attendanceId: number; lateMinutes: number } | null;
  submitLateReason: (reason: string) => void;
  lateReasonPending: boolean;
}

export function HomeModals({
  confirmAction,
  confirmCopy,
  setConfirmAction,
  runConfirmedAction,
  checkInSuccess,
  lateReasonRequest,
  submitLateReason,
  lateReasonPending,
}: HomeModalsProps) {
  const { t, language } = useLanguage();
  const [lateReason, setLateReason] = useState("");

  const modalTitle = confirmAction === "checkin" 
    ? t.confirmCheckInTitle 
    : confirmAction === "checkout" 
    ? t.confirmCheckOutTitle 
    : confirmCopy?.title;

  const modalBody = confirmAction === "checkin"
    ? t.confirmCheckInBody
    : confirmAction === "checkout"
    ? t.confirmCheckOutBody
    : confirmCopy?.body;

  return (
    <>
      {/* Confirmation Modal Dialog */}
      {confirmAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="w-full max-w-sm rounded-[2rem] bg-white p-6 shadow-2xl border border-slate-100 animate-in zoom-in-95 duration-200">
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-teal-50 text-teal-700 mx-auto">
              <Check size={28} strokeWidth={3} />
            </div>
            <h2 className="text-xl font-black text-slate-900 text-center">
              {modalTitle}
            </h2>
            <p className="mt-2 text-xs font-semibold leading-relaxed text-slate-500 text-center">
              {modalBody}
            </p>
            <div className="mt-6 grid grid-cols-2 gap-2.5">
              <Button
                variant="outline"
                className="h-12 rounded-2xl font-bold text-sm border-slate-200"
                onClick={() => setConfirmAction(cancelAttendanceConfirmation())}
              >
                {t.modalCancel}
              </Button>
              <Button
                className="btn-primary-teal h-12 rounded-2xl font-black text-sm"
                onClick={runConfirmedAction}
              >
                {t.modalConfirm}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Success Popup Overlay */}
      {checkInSuccess && (
        <div className="success-overlay" role="status" aria-live="polite">
          <div className="success-card">
            <div className="success-icon-wrapper">
              <Check size={38} strokeWidth={3.5} />
            </div>
            <h2 className="text-2xl font-black text-emerald-900">{t.successCheckInTitle}</h2>
            <p className="mt-1 text-sm font-semibold text-emerald-700">
              {t.successCheckInSub}
            </p>
            <div className="mt-4 rounded-xl bg-emerald-50 py-2 px-3 text-xs font-bold text-emerald-800 border border-emerald-200">
              {thaiTimeShort(new Date())} น.
            </div>
          </div>
        </div>
      )}

      {lateReasonRequest && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-3xl border-2 border-amber-300 bg-white p-6 shadow-2xl">
            <h2 className="text-xl font-black text-amber-900">
              {language === "my" ? "အလုပ်နောက်ကျရခြင်းအကြောင်း ပြောပါ" : "กรุณาระบุเหตุผลที่มาสาย"}
            </h2>
            <p className="mt-2 text-sm font-semibold text-slate-600">
              {language === "my" ? `သတ်မှတ်ချိန်ထက် ${lateReasonRequest.lateMinutes} မိနစ် နောက်ကျပါသည်။ အုပ်ချုပ်သူ စစ်ဆေးရန် အကြောင်းပြချက် ဖြည့်ပါ။` : `คุณเข้าสายเกินเวลาที่กำหนด ${lateReasonRequest.lateMinutes} นาที เหตุผลจะถูกส่งให้แอดมินพิจารณา`}
            </p>
            <textarea autoFocus rows={4} maxLength={500} value={lateReason} onChange={(event) => setLateReason(event.target.value)} className="mt-4 w-full rounded-2xl border-2 border-slate-300 p-3 text-base font-semibold outline-none focus:border-amber-500" placeholder={language === "my" ? "အကြောင်းပြချက်..." : "เช่น รถติด, รถเสีย, ย้ายไซต์งาน..."} />
            <Button disabled={lateReason.trim().length < 3 || lateReasonPending} onClick={() => submitLateReason(lateReason.trim())} className="mt-4 h-12 w-full rounded-2xl bg-amber-600 text-base font-black text-white hover:bg-amber-700">
              {lateReasonPending ? (language === "my" ? "ပို့နေသည်..." : "กำลังส่ง...") : (language === "my" ? "အကြောင်းပြချက် ပို့ရန်" : "ส่งเหตุผลให้แอดมิน")}
            </Button>
          </div>
        </div>
      )}

      <footer className="mt-8 text-center text-xs font-semibold text-slate-400">
        <p>Demo Check-In · Smart Attendance System</p>
      </footer>
    </>
  );
}
